import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { InstagramPostPreview } from '../components/instagram-post-preview';
import { useConnections } from '../hooks/use-connections';
import { usePublishRun } from '../hooks/use-publish';
import { useRun } from '../hooks/use-runs';
import {
  cardsForFormat,
  publishableFormats,
  publishReadiness,
  resolveTargetAccount,
} from '../lib/publish-target';
import type { FormatId } from '../types/api';

/** Human label for each publishable format tab. */
const FORMAT_LABELS: Partial<Record<FormatId, string>> = {
  ig_square: 'Square',
  ig_portrait: 'Portrait',
};

export function RunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const runQ = useRun(id ?? null);
  const publish = usePublishRun(id ?? null);
  // run.country is null for the global brand account; useConnections wants
  // 'global' for that slot. Until the run loads we query 'global' — it is
  // re-keyed and refetched once the real country is known.
  const connectionsQ = useConnections(runQ.data?.run.country ?? 'global');

  // The publishable formats present in the loaded run. Memoised on the cards
  // reference (stable per query result) so the array identity only changes
  // when the run data actually changes.
  const cards = runQ.data?.cards;
  const formats = useMemo(() => publishableFormats(cards ?? []), [cards]);

  // The format whose carousel is previewed and published. `null` until the
  // admin picks one; a safe `activeFormat` is derived below so we never depend
  // on state initialised from not-yet-loaded data.
  const [selectedFormat, setSelectedFormat] = useState<FormatId | null>(null);
  // `null` until the admin edits the caption; the effective caption then falls
  // back to the run's generated caption.
  const [captionDraft, setCaptionDraft] = useState<string | null>(null);

  if (!id) return null;
  if (runQ.isLoading) return <div className="muted">Loading…</div>;
  if (runQ.error) return <div className="error">{runQ.error.message}</div>;
  if (!runQ.data) return <div className="muted">Run not found.</div>;

  const { run, cards: runCards, posts } = runQ.data;

  // Derive the active format defensively: the run loads after the first render,
  // so `selectedFormat` may still be null or a format no longer present.
  const activeFormat =
    selectedFormat && formats.includes(selectedFormat)
      ? selectedFormat
      : (formats[0] ?? null);
  const igCards = activeFormat ? cardsForFormat(runCards, activeFormat) : [];

  const effectiveCaption = captionDraft ?? run.caption ?? '';
  const captionEdited = captionDraft !== null;
  const captionPresent = Boolean(effectiveCaption.trim());

  const target = resolveTargetAccount(run.country, connectionsQ.data ?? []);
  const connLoading = connectionsQ.isLoading;

  // Show the publish panel for any run whose assets rendered, including runs
  // that rendered but then failed at the publish step — those can be retried
  // manually. `rendered_at` is the authoritative "assets exist" signal.
  const canPublish =
    run.status === 'rendered' ||
    run.status === 'published' ||
    run.rendered_at !== null;
  const readiness = publishReadiness({
    status: run.status,
    renderedAt: run.rendered_at,
    captionPresent,
    igCardCount: igCards.length,
    // Stay optimistic while connections load so the "not connected" blocker
    // doesn't flash before the lookup resolves.
    connected: connLoading ? true : target.connected,
  });
  const publishDisabled =
    !readiness.ready || publish.isPending || connLoading;

  function onPublish() {
    if (publishDisabled) return;
    const captionOverride =
      captionEdited && effectiveCaption.trim() ? effectiveCaption : undefined;
    publish.mutate({
      platforms: ['instagram'],
      country: run.country,
      ...(activeFormat ? { format: activeFormat } : {}),
      ...(captionOverride ? { captionOverride } : {}),
    });
  }

  return (
    <div className="page run-detail">
      <header className="page-head">
        <Link to="/runs" className="link">
          ← All runs
        </Link>
        <h1>Run · {run.id.slice(0, 8)}…</h1>
        <div className="muted">
          {new Date(run.created_at).toLocaleString()} ·{' '}
          <span className={`pill pill-${run.status}`}>{run.status}</span> ·{' '}
          {run.platforms.join(', ')} · {run.article_ids.length} articles
        </div>
        {run.error ? <div className="error">{run.error}</div> : null}
      </header>

      <div className="run-detail-grid">
        <section className="run-preview-col">
          {formats.length > 1 ? (
            <div
              className="format-tabs"
              role="tablist"
              aria-label="Publish format"
            >
              {formats.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={f === activeFormat}
                  className={
                    f === activeFormat
                      ? 'format-tab format-tab-active'
                      : 'format-tab'
                  }
                  onClick={() => setSelectedFormat(f)}
                >
                  {FORMAT_LABELS[f] ?? f}
                </button>
              ))}
            </div>
          ) : null}

          <InstagramPostPreview
            cards={igCards}
            caption={effectiveCaption}
            handle={target.handle}
          />
        </section>

        <section className="publish-panel">
          <h2>Publish to Instagram</h2>

          {!canPublish ? (
            <p className="muted">
              {run.status === 'failed'
                ? 'This run failed — see the error above. Re-render it before publishing.'
                : `This run is ${run.status}. Publishing is available once it has rendered.`}
            </p>
          ) : (
            <>
          <div className="caption-editor">
            <label htmlFor="caption-editor-input">Caption</label>
            <textarea
              id="caption-editor-input"
              className="caption-editor-input"
              rows={5}
              value={effectiveCaption}
              onChange={(e) => setCaptionDraft(e.target.value)}
            />
            {captionEdited ? (
              <button
                type="button"
                className="caption-reset link"
                onClick={() => setCaptionDraft(null)}
              >
                Reset to generated
              </button>
            ) : null}
          </div>

          <div className="target-account">
            <span className="target-label">Target account</span>
            {connLoading ? (
              <span className="target-value muted">Checking connection…</span>
            ) : target.connected ? (
              <span className="target-value">
                <span className="target-flag" aria-hidden="true">
                  {target.flag}
                </span>
                {target.label} ·{' '}
                <span className="target-handle">{target.handle}</span>
              </span>
            ) : (
              <span className="target-value target-missing">
                <span className="target-flag" aria-hidden="true">
                  {target.flag}
                </span>
                No Instagram account connected for {target.label}.{' '}
                <Link
                  to={`/connections?country=${target.countryKey}`}
                  className="link"
                >
                  Connect one →
                </Link>
              </span>
            )}
          </div>

          {readiness.blockers.length > 0 ? (
            <ul className="readiness-blockers">
              {readiness.blockers.map((b) => (
                <li key={b} className="readiness-blocker">
                  {b}
                </li>
              ))}
            </ul>
          ) : null}

          <button
            className="btn-primary"
            disabled={publishDisabled}
            onClick={onPublish}
          >
            {publish.isPending ? 'Publishing…' : 'Publish to Instagram'}
          </button>

          {publish.error ? (
            <div className="error">{publish.error.message}</div>
          ) : null}
          {publish.data ? (
            <div className="publish-result muted">
              {publish.data.posts.every((p) => p.status === 'published')
                ? 'Published — see Posts below.'
                : 'Publish finished with errors — see Posts below.'}
            </div>
          ) : null}
            </>
          )}
        </section>
      </div>

      {posts.length > 0 ? (
        <section className="posts-panel">
          <h2>Posts</h2>
          <table className="runs-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Status</th>
                <th>URL</th>
                <th>Posted</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id}>
                  <td>{p.platform}</td>
                  <td>
                    <span className={`pill pill-${p.status}`}>{p.status}</span>
                  </td>
                  <td>
                    {p.post_url ? (
                      <a
                        href={p.post_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link"
                      >
                        open ↗
                      </a>
                    ) : p.error ? (
                      <span className="dim small">{p.error}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="dim mono">
                    {p.posted_at
                      ? new Date(p.posted_at).toLocaleString()
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  );
}
